/**
 * Interactive designer canvas for fields and recursively nested layouts.
 *
 * Drag payloads contain stable ids/types, not raw HTML. Drop handlers delegate
 * structural changes to designer-utils.ts to keep tree manipulation testable.
 */
import { Fragment } from 'react';
import type { DragEvent, MouseEvent } from 'react';
import type { DynamicFormDefinition, FormColumn, FormField, FormRow, FormTab } from '../../models/form';
import { createField, createLayout } from '../../legacy/schema-codec';
import {
  addFieldToColumn,
  addLayoutToColumn,
  addLayoutToPanel,
  addLayoutToTable,
  addLayoutToTab,
  addNestedField,
  duplicateField,
  duplicateLayout,
  findField,
  moveField,
  moveLayout,
  removeField,
  removeLayout,
  updateField,
  updateLayout,
  type DesignerSelection
} from './designer-utils';
import { FieldPreview } from './FieldPreview';
import { clearDesignerDrag, readDesignerDrag, writeDesignerDrag } from './drag-payload';

interface Props {
  form: DynamicFormDefinition;
  activeTab: string;
  selection?: DesignerSelection;
  viewport: 'xs' | 'sm' | 'md' | 'lg';
  onChange: (form: DynamicFormDefinition) => void;
  onSelect: (selection?: DesignerSelection) => void;
  onChangeTab: (name: string) => void;
}

function readDrag(event: DragEvent) {
  return readDesignerDrag(event);
}

function selectLayout(layoutId: string, onSelect: Props['onSelect']) {
  return (event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    onSelect({ kind: 'layout', id: layoutId });
  };
}

function beginLayoutDrag(layoutId: string, event: DragEvent<HTMLElement>) {
  event.stopPropagation();
  writeDesignerDrag(event, { kind: 'move-layout', layoutId }, 'move');
}

const TABLE_DATA_FIELD_TYPES = new Set<FormField['type']>(['input', 'textarea', 'select', 'checkbox', 'radio', 'fileupload']);

function isTableDataField(field: Pick<FormField, 'type'>): boolean {
  return TABLE_DATA_FIELD_TYPES.has(field.type);
}


const PANEL_REQUIRED_FIELD_TYPES = new Set<FormField['type']>(['input', 'textarea', 'select', 'checkbox', 'radio', 'fileupload', 'table', 'simplecaptcha', 'captcha']);

function inheritPanelRequired(field: FormField, required: boolean): FormField {
  if (!required) return field;
  const requireRows = (rows?: FormRow[]): FormRow[] | undefined => rows?.map((row) => requireLayoutFields(row));
  return {
    ...field,
    validation: PANEL_REQUIRED_FIELD_TYPES.has(field.type) || field.type === 'panel'
      ? { ...(field.validation ?? {}), required: true }
      : field.validation,
    children: field.children?.map((child) => inheritPanelRequired(child, true)),
    tableColumns: field.tableColumns?.map((child) => inheritPanelRequired(child, true)),
    panelRows: requireRows(field.panelRows),
    tableRows: requireRows(field.tableRows)
  };
}

function requireLayoutFields(row: FormRow): FormRow {
  return {
    ...row,
    columns: row.columns.map((column) => ({
      ...column,
      fields: column.fields.map((child) => inheritPanelRequired(child, true)),
      rows: column.rows?.map((childRow) => requireLayoutFields(childRow))
    }))
  };
}
function fieldAsSingleColumnLayout(field: FormField): FormRow {
  const layout = createLayout(1);
  const column = layout.columns[0];
  if (column) column.fields.push(field);
  return layout;
}

function tabVisualStyle(tab: FormTab) {
  return tab.color || tab.textColor ? { backgroundColor: tab.color || undefined, color: tab.textColor || undefined } : undefined;
}


function countNestedFields(field: FormField): number {
  const children = [...(field.children ?? []), ...(field.tableColumns ?? [])];
  const direct = children.reduce((total, child) => total + 1 + countNestedFields(child), 0);
  const panelRows = field.panelRows?.reduce((total, row) => {
    const nested = countLayoutContents(row);
    return total + nested.fields;
  }, 0) ?? 0;
  const tableRows = field.tableRows?.reduce((total, row) => {
    const nested = countLayoutContents(row);
    return total + nested.fields;
  }, 0) ?? 0;
  return direct + panelRows + tableRows;
}

function countLayoutContents(row: FormRow): { fields: number; layouts: number } {
  let fields = 0;
  let layouts = 0;
  for (const column of row.columns) {
    for (const field of column.fields) fields += 1 + countNestedFields(field);
    for (const child of column.rows ?? []) {
      layouts += 1;
      const nested = countLayoutContents(child);
      fields += nested.fields;
      layouts += nested.layouts;
    }
  }
  return { fields, layouts };
}

function confirmContainerFieldRemoval(form: DynamicFormDefinition, fieldId: string): boolean {
  const field = findField(form, fieldId);
  if (!field || (field.type !== 'panel' && field.type !== 'table')) return true;
  const nested = countNestedFields(field);
  if (!nested) return true;
  return window.confirm(`این ${field.type === 'panel' ? 'پنل' : 'جدول'} شامل ${nested} کنترل داخلی است. همه آن‌ها حذف شوند؟`);
}

function InsertZone({ label = 'رها کنید', onDrop }: { label?: string; onDrop: (event: DragEvent) => void }) {
  return <div
    className="df-field-insert-zone"
    onDragOver={(event) => { event.preventDefault(); event.stopPropagation(); const payload = readDrag(event); event.dataTransfer.dropEffect = payload?.kind === 'field' || payload?.kind === 'layout' ? 'copy' : 'move'; }}
    onDrop={onDrop}
  ><span>{label}</span></div>;
}

interface LayoutPreviewProps {
  row: FormRow;
  tabName: string;
  form: DynamicFormDefinition;
  selection?: DesignerSelection;
  depth: number;
  requiredByPanel?: boolean;
  onChange: Props['onChange'];
  onSelect: Props['onSelect'];
}

function LayoutPreview({ row, tabName, form, selection, depth, requiredByPanel = false, onChange, onSelect }: LayoutPreviewProps) {
  const selected = selection?.kind === 'layout' && selection.id === row.id;

  const dropFieldAt = (event: DragEvent, column: FormColumn, index: number) => {
    event.preventDefault();
    event.stopPropagation();
    const payload = readDrag(event);
    if (!payload) return;
    if (payload.kind === 'field') {
      const field = inheritPanelRequired(createField(payload.type, payload.inputType), requiredByPanel);
      onChange(addFieldToColumn(form, tabName, row.id, column.id, field, index));
      onSelect({ kind: 'field', id: field.id });
    } else if (payload.kind === 'move') {
      const source = requiredByPanel
        ? updateField(form, payload.fieldId, (field) => inheritPanelRequired(field, true))
        : form;
      onChange(moveField(source, payload.fieldId, { tabName, rowId: row.id, columnId: column.id, index }));
      onSelect({ kind: 'field', id: payload.fieldId });
    }
  };

  const dropColumn = (event: DragEvent, column: FormColumn) => {
    event.preventDefault();
    event.stopPropagation();
    const payload = readDrag(event);
    if (!payload) return;

    if (payload.kind === 'field' || payload.kind === 'move') {
      dropFieldAt(event, column, column.fields.length);
    } else if (payload.kind === 'layout') {
      const layout = createLayout(Number(payload.columns));
      onChange(addLayoutToColumn(form, tabName, column.id, layout));
      onSelect({ kind: 'layout', id: layout.id });
    } else if (payload.kind === 'move-layout') {
      const source = requiredByPanel
        ? updateLayout(form, payload.layoutId, (layout) => requireLayoutFields(layout))
        : form;
      onChange(moveLayout(source, payload.layoutId, { tabName, columnId: column.id }));
      onSelect({ kind: 'layout', id: payload.layoutId });
    }
  };

  const nestedFieldDrop = (parentId: string, table: boolean, event: DragEvent, index?: number) => {
    event.preventDefault();
    event.stopPropagation();
    const payload = readDrag(event);
    if (!payload) return;
    if (payload.kind === 'field') {
      const field = inheritPanelRequired(createField(payload.type, payload.inputType), requiredByPanel);
      if (table && !isTableDataField(field)) return;
      onChange(addNestedField(form, parentId, field, table, index));
      onSelect({ kind: 'field', id: field.id });
    } else if (payload.kind === 'move') {
      const moving = findField(form, payload.fieldId);
      if (table && moving && !isTableDataField(moving)) return;
      const source = requiredByPanel
        ? updateField(form, payload.fieldId, (field) => inheritPanelRequired(field, true))
        : form;
      onChange(moveField(source, payload.fieldId, { tabName, rowId: row.id, columnId: '', parentFieldId: parentId, table, index }));
      onSelect({ kind: 'field', id: payload.fieldId });
    }
  };

  const dropPanelContent = (parentId: string, event: DragEvent, index?: number) => {
    event.preventDefault();
    event.stopPropagation();
    const payload = readDrag(event);
    if (!payload) return;
    if (payload.kind === 'field') {
      const panelRequired = requiredByPanel || Boolean(findField(form, parentId)?.validation?.required);
      const field = inheritPanelRequired(createField(payload.type, payload.inputType), panelRequired);
      onChange(addNestedField(form, parentId, field, false, index));
      onSelect({ kind: 'field', id: field.id });
    } else if (payload.kind === 'move') {
      const panelRequired = requiredByPanel || Boolean(findField(form, parentId)?.validation?.required);
      const source = panelRequired
        ? updateField(form, payload.fieldId, (field) => inheritPanelRequired(field, true))
        : form;
      onChange(moveField(source, payload.fieldId, { tabName, rowId: row.id, columnId: '', parentFieldId: parentId, table: false, index }));
      onSelect({ kind: 'field', id: payload.fieldId });
    } else if (payload.kind === 'layout') {
      const layout = createLayout(Number(payload.columns));
      onChange(addLayoutToPanel(form, parentId, layout));
      onSelect({ kind: 'layout', id: layout.id });
    } else if (payload.kind === 'move-layout') {
      const panelRequired = requiredByPanel || Boolean(findField(form, parentId)?.validation?.required);
      const source = panelRequired
        ? updateLayout(form, payload.layoutId, (layout) => requireLayoutFields(layout))
        : form;
      onChange(moveLayout(source, payload.layoutId, { tabName, parentFieldId: parentId }));
      onSelect({ kind: 'layout', id: payload.layoutId });
    }
  };

  const dropPanelLayoutAt = (parentId: string, event: DragEvent, index: number) => {
    event.preventDefault();
    event.stopPropagation();
    const payload = readDrag(event);
    if (!payload) return;
    if (payload.kind === 'layout') {
      const layout = createLayout(Number(payload.columns));
      onChange(addLayoutToPanel(form, parentId, layout, index));
      onSelect({ kind: 'layout', id: layout.id });
    } else if (payload.kind === 'move-layout') {
      const panelRequired = requiredByPanel || Boolean(findField(form, parentId)?.validation?.required);
      const source = panelRequired
        ? updateLayout(form, payload.layoutId, (layout) => requireLayoutFields(layout))
        : form;
      onChange(moveLayout(source, payload.layoutId, { tabName, parentFieldId: parentId, index }));
      onSelect({ kind: 'layout', id: payload.layoutId });
    } else if (payload.kind === 'field' && payload.type === 'spacer') {
      const field = createField('spacer');
      const layout = fieldAsSingleColumnLayout(field);
      onChange(addLayoutToPanel(form, parentId, layout, index));
      onSelect({ kind: 'field', id: field.id });
    } else if (payload.kind === 'move') {
      const moving = findField(form, payload.fieldId);
      if (moving?.type !== 'spacer') return;
      const layout = createLayout(1);
      const column = layout.columns[0];
      if (!column) return;
      const withLayout = addLayoutToPanel(form, parentId, layout, index);
      onChange(moveField(withLayout, payload.fieldId, { tabName, rowId: layout.id, columnId: column.id, index: 0 }));
      onSelect({ kind: 'field', id: payload.fieldId });
    }
  };

  const renderPanelLayouts = (panel: FormField) => {
    const layouts = panel.panelRows ?? [];
    return <div className="df-panel-layouts">
      {layouts.map((panelRow, index) => <div className="df-layout-list-item" key={panelRow.id}>
        <div className="df-layout-insert-zone" onDragOver={(event) => { event.preventDefault(); event.stopPropagation(); }} onDrop={(event) => dropPanelLayoutAt(panel.id, event, index)}>لایه یا کنترل فاصله را داخل پنل اینجا قرار دهید</div>
        <LayoutPreview row={panelRow} tabName={tabName} form={form} selection={selection} depth={depth + 1} requiredByPanel={requiredByPanel || Boolean(panel.validation?.required)} onChange={onChange} onSelect={onSelect} />
      </div>)}
      <div className="df-layout-insert-zone df-panel-layout-drop" onDragOver={(event) => { event.preventDefault(); event.stopPropagation(); }} onDrop={(event) => dropPanelLayoutAt(panel.id, event, layouts.length)}>{layouts.length ? 'لایه یا کنترل فاصله را در انتهای پنل قرار دهید' : 'لایه یا کنترل فاصله را داخل پنل رها کنید'}</div>
    </div>;
  };

  const dropTableContent = (tableId: string, event: DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    const payload = readDrag(event);
    if (!payload) return;
    const tableRequired = requiredByPanel || Boolean(findField(form, tableId)?.validation?.required);
    if (payload.kind === 'layout') {
      const layout = createLayout(Number(payload.columns));
      onChange(addLayoutToTable(form, tableId, tableRequired ? requireLayoutFields(layout) : layout));
      onSelect({ kind: 'layout', id: layout.id });
      return;
    }
    if (payload.kind === 'move-layout') {
      const source = tableRequired
        ? updateLayout(form, payload.layoutId, (layout) => requireLayoutFields(layout))
        : form;
      onChange(moveLayout(source, payload.layoutId, { tabName, parentFieldId: tableId }));
      onSelect({ kind: 'layout', id: payload.layoutId });
      return;
    }
    if (payload.kind === 'field') {
      const field = inheritPanelRequired(createField(payload.type, payload.inputType), tableRequired);
      if (isTableDataField(field)) {
        onChange(addNestedField(form, tableId, field, true));
      } else {
        const layout = createLayout(1);
        layout.columns[0]?.fields.push(field);
        onChange(addLayoutToTable(form, tableId, layout));
      }
      onSelect({ kind: 'field', id: field.id });
      return;
    }
    if (payload.kind === 'move') {
      const moving = findField(form, payload.fieldId);
      if (!moving) return;
      if (isTableDataField(moving)) {
        const source = tableRequired
          ? updateField(form, payload.fieldId, (field) => inheritPanelRequired(field, true))
          : form;
        onChange(moveField(source, payload.fieldId, { tabName, rowId: row.id, columnId: '', parentFieldId: tableId, table: true }));
      } else {
        const table = findField(form, tableId);
        const targetRow = table?.tableRows?.[0];
        const targetColumn = targetRow?.columns[0];
        if (targetRow && targetColumn) {
          onChange(moveField(form, payload.fieldId, { tabName, rowId: targetRow.id, columnId: targetColumn.id }));
        } else {
          const layout = createLayout(1);
          const column = layout.columns[0];
          if (!column) return;
          const withLayout = addLayoutToTable(form, tableId, layout);
          onChange(moveField(withLayout, payload.fieldId, { tabName, rowId: layout.id, columnId: column.id }));
        }
      }
      onSelect({ kind: 'field', id: payload.fieldId });
    }
  };

  const dropTableLayoutAt = (tableId: string, event: DragEvent, index: number) => {
    event.preventDefault();
    event.stopPropagation();
    const payload = readDrag(event);
    if (!payload) return;
    const tableRequired = requiredByPanel || Boolean(findField(form, tableId)?.validation?.required);
    if (payload.kind === 'layout') {
      const layout = createLayout(Number(payload.columns));
      onChange(addLayoutToTable(form, tableId, tableRequired ? requireLayoutFields(layout) : layout, index));
      onSelect({ kind: 'layout', id: layout.id });
    } else if (payload.kind === 'move-layout') {
      const source = tableRequired
        ? updateLayout(form, payload.layoutId, (layout) => requireLayoutFields(layout))
        : form;
      onChange(moveLayout(source, payload.layoutId, { tabName, parentFieldId: tableId, index }));
      onSelect({ kind: 'layout', id: payload.layoutId });
    } else if (payload.kind === 'field' && payload.type === 'spacer') {
      const field = createField('spacer');
      const layout = fieldAsSingleColumnLayout(field);
      onChange(addLayoutToTable(form, tableId, layout, index));
      onSelect({ kind: 'field', id: field.id });
    } else if (payload.kind === 'move') {
      const moving = findField(form, payload.fieldId);
      if (moving?.type !== 'spacer') return;
      const layout = createLayout(1);
      const column = layout.columns[0];
      if (!column) return;
      const withLayout = addLayoutToTable(form, tableId, layout, index);
      onChange(moveField(withLayout, payload.fieldId, { tabName, rowId: layout.id, columnId: column.id, index: 0 }));
      onSelect({ kind: 'field', id: payload.fieldId });
    }
  };

  const renderTableLayouts = (table: FormField) => {
    const layouts = table.tableRows ?? [];
    return <div className="df-table-layouts">
      {layouts.map((tableRow, index) => <div className="df-layout-list-item" key={tableRow.id}>
        <div className="df-layout-insert-zone" onDragOver={(event) => { event.preventDefault(); event.stopPropagation(); }} onDrop={(event) => dropTableLayoutAt(table.id, event, index)}>لایه یا کنترل فاصله را در بخش ورود ردیف اینجا قرار دهید</div>
        <LayoutPreview row={tableRow} tabName={tabName} form={form} selection={selection} depth={depth + 1} requiredByPanel={requiredByPanel || Boolean(table.validation?.required)} onChange={onChange} onSelect={onSelect} />
      </div>)}
      <div className="df-layout-insert-zone df-table-layout-drop" onDragOver={(event) => { event.preventDefault(); event.stopPropagation(); }} onDrop={(event) => dropTableLayoutAt(table.id, event, layouts.length)}>{layouts.length ? 'لایه یا کنترل فاصله را در انتهای بخش ورود ردیف قرار دهید' : 'لایه یا کنترل نمایشی را اینجا رها کنید'}</div>
    </div>;
  };

  const dropLayoutAt = (event: DragEvent, columnId: string, index: number) => {
    event.preventDefault();
    event.stopPropagation();
    const payload = readDrag(event);
    if (!payload) return;
    if (payload.kind === 'layout') {
      const layout = createLayout(Number(payload.columns));
      onChange(addLayoutToColumn(form, tabName, columnId, layout, index));
      onSelect({ kind: 'layout', id: layout.id });
    } else if (payload.kind === 'move-layout') {
      const source = requiredByPanel
        ? updateLayout(form, payload.layoutId, (layout) => requireLayoutFields(layout))
        : form;
      onChange(moveLayout(source, payload.layoutId, { tabName, columnId, index }));
      onSelect({ kind: 'layout', id: payload.layoutId });
    } else if (payload.kind === 'field' && payload.type === 'spacer') {
      const field = createField('spacer');
      const layout = fieldAsSingleColumnLayout(field);
      onChange(addLayoutToColumn(form, tabName, columnId, layout, index));
      onSelect({ kind: 'field', id: field.id });
    } else if (payload.kind === 'move') {
      const moving = findField(form, payload.fieldId);
      if (moving?.type !== 'spacer') return;
      const layout = createLayout(1);
      const column = layout.columns[0];
      if (!column) return;
      const withLayout = addLayoutToColumn(form, tabName, columnId, layout, index);
      onChange(moveField(withLayout, payload.fieldId, { tabName, rowId: layout.id, columnId: column.id, index: 0 }));
      onSelect({ kind: 'field', id: payload.fieldId });
    }
  };

  return <div
    className={`df-layout-shell ${selected ? 'is-selected' : ''} ${depth > 0 ? 'is-nested' : ''}`}
    data-layout-id={row.id}
    onClick={selectLayout(row.id, onSelect)}
  >
    <div className="df-layout-toolbar">
      <span className="df-drag-handle" draggable onDragStart={(event) => beginLayoutDrag(row.id, event)} onDragEnd={clearDesignerDrag} title="برای جابه‌جایی بکشید" aria-label="جابه‌جایی لایه">⋮⋮</span>
      <span>لایه {row.columns.length} ستونه</span>
      <button type="button" title="تکثیر لایه" aria-label="تکثیر لایه" onClick={(event) => {
        event.stopPropagation();
        onChange(duplicateLayout(form, row.id));
      }}>⧉</button>
      <button type="button" title="حذف لایه" aria-label="حذف لایه" onClick={(event) => {
        event.stopPropagation();
        const contents = countLayoutContents(row);
        if ((contents.fields > 0 || contents.layouts > 0) && !window.confirm(`این لایه شامل ${contents.fields} کنترل و ${contents.layouts} لایه داخلی است. همه محتوا حذف شود؟`)) return;
        const result = removeLayout(form, row.id);
        onChange(result.form);
        if (selection?.kind === 'layout' && selection.id === row.id) onSelect(undefined);
      }}>×</button>
    </div>

    <div className="row df-designer-row df-form-row">
      {row.columns.map((column) => {
        const hasContent = column.fields.length > 0 || Boolean(column.rows?.length);
        return <div
          key={column.id}
          className={`col-md-${column.span} df-designer-column df-form-col df-col-span-${column.span}`}
          data-column-id={column.id}
          onDragOver={(event) => {
            event.preventDefault();
            event.stopPropagation();
            const payload = readDrag(event);
            event.dataTransfer.dropEffect = payload?.kind === 'field' || payload?.kind === 'layout' ? 'copy' : 'move';
          }}
          onDrop={(event) => dropColumn(event, column)}
        >
          {column.fields.map((field, index) => <Fragment key={field.id}>
            <InsertZone onDrop={(event) => dropFieldAt(event, column, index)} />
            <FieldPreview
              field={field}
              selectedFieldId={selection?.kind === 'field' ? selection.id : undefined}
              onSelect={(fieldId) => onSelect({ kind: 'field', id: fieldId })}
              onRemove={(fieldId) => {
                if (!confirmContainerFieldRemoval(form, fieldId)) return;
                const result = removeField(form, fieldId);
                onChange(result.form);
                if (selection?.kind === 'field' && selection.id === fieldId) onSelect(undefined);
              }}
              onDuplicate={(fieldId) => onChange(duplicateField(form, fieldId))}
              onAddNested={nestedFieldDrop}
              onDropPanelContent={dropPanelContent}
              onDropTableContent={dropTableContent}
              renderPanelLayouts={renderPanelLayouts}
              renderTableLayouts={renderTableLayouts}
            />
          </Fragment>)}
          <InsertZone label={column.fields.length ? 'انتقال کنترل به انتهای ستون' : 'کنترل را اینجا رها کنید'} onDrop={(event) => dropFieldAt(event, column, column.fields.length)} />

          {column.rows?.map((childRow, index) => <div className="df-layout-list-item" key={childRow.id}>
            <div className="df-layout-insert-zone" onDragOver={(event) => { event.preventDefault(); event.stopPropagation(); }} onDrop={(event) => dropLayoutAt(event, column.id, index)}>لایه یا کنترل فاصله را اینجا قرار دهید</div>
            <LayoutPreview
              row={childRow}
              tabName={tabName}
              form={form}
              selection={selection}
              depth={depth + 1}
              requiredByPanel={requiredByPanel}
              onChange={onChange}
              onSelect={onSelect}
            />
          </div>)}
          {column.rows?.length ? <div className="df-layout-insert-zone" onDragOver={(event) => { event.preventDefault(); event.stopPropagation(); }} onDrop={(event) => dropLayoutAt(event, column.id, column.rows?.length ?? 0)}>لایه یا کنترل فاصله را در انتها قرار دهید</div> : null}

          {!hasContent ? <div className="df-drop-hint">کنترل یا لایه را اینجا رها کنید</div> : null}
        </div>;
      })}
    </div>
  </div>;
}

export function DesignerCanvas({ form, activeTab, selection, viewport, onChange, onSelect, onChangeTab }: Props) {
  const tab = form.Tabs.find((item) => item.name === activeTab) ?? form.Tabs[0];
  if (!tab) return null;

  const dropCanvas = (event: DragEvent) => {
    event.preventDefault();
    const payload = readDrag(event);
    if (!payload) return;
    if (payload.kind === 'layout') {
      const layout = createLayout(Number(payload.columns));
      onChange(addLayoutToTab(form, tab.name, layout));
      onSelect({ kind: 'layout', id: layout.id });
    } else if (payload.kind === 'move-layout') {
      onChange(moveLayout(form, payload.layoutId, { tabName: tab.name }));
      onSelect({ kind: 'layout', id: payload.layoutId });
    } else if (payload.kind === 'field' && payload.type === 'spacer') {
      const field = createField('spacer');
      const layout = fieldAsSingleColumnLayout(field);
      onChange(addLayoutToTab(form, tab.name, layout));
      onSelect({ kind: 'field', id: field.id });
    } else if (payload.kind === 'move') {
      const moving = findField(form, payload.fieldId);
      if (moving?.type !== 'spacer') return;
      const layout = createLayout(1);
      const column = layout.columns[0];
      if (!column) return;
      const withLayout = addLayoutToTab(form, tab.name, layout);
      onChange(moveField(withLayout, payload.fieldId, { tabName: tab.name, rowId: layout.id, columnId: column.id, index: 0 }));
      onSelect({ kind: 'field', id: payload.fieldId });
    }
  };

  const dropLayoutAtTab = (event: DragEvent, index: number) => {
    event.preventDefault();
    event.stopPropagation();
    const payload = readDrag(event);
    if (!payload) return;
    if (payload.kind === 'layout') {
      const layout = createLayout(Number(payload.columns));
      onChange(addLayoutToTab(form, tab.name, layout, index));
      onSelect({ kind: 'layout', id: layout.id });
    } else if (payload.kind === 'move-layout') {
      onChange(moveLayout(form, payload.layoutId, { tabName: tab.name, index }));
      onSelect({ kind: 'layout', id: payload.layoutId });
    } else if (payload.kind === 'field' && payload.type === 'spacer') {
      const field = createField('spacer');
      const layout = fieldAsSingleColumnLayout(field);
      onChange(addLayoutToTab(form, tab.name, layout, index));
      onSelect({ kind: 'field', id: field.id });
    } else if (payload.kind === 'move') {
      const moving = findField(form, payload.fieldId);
      if (moving?.type !== 'spacer') return;
      const layout = createLayout(1);
      const column = layout.columns[0];
      if (!column) return;
      const withLayout = addLayoutToTab(form, tab.name, layout, index);
      onChange(moveField(withLayout, payload.fieldId, { tabName: tab.name, rowId: layout.id, columnId: column.id, index: 0 }));
      onSelect({ kind: 'field', id: payload.fieldId });
    }
  };

  return <main
    className={`df-designer-canvas df-viewport-${viewport}`}
    onDragOver={(event) => event.preventDefault()}
    onDrop={dropCanvas}
    onClick={() => onSelect(undefined)}
  >
    <ul className="nav nav-tabs df-tabs" role="tablist">
      {form.Tabs.map((item: FormTab) => <li key={item.name} className={`nav-item ${item.name === tab.name ? 'active' : ''}`} role="presentation">
        <button type="button" style={tabVisualStyle(item)} className={`nav-link ${item.name === tab.name ? 'active' : ''}`} role="tab" aria-selected={item.name === tab.name} onClick={(event) => {
          event.stopPropagation();
          onChangeTab(item.name);
          onSelect(undefined);
        }}>{item.title}</button>
      </li>)}
    </ul>

    <div className="df-canvas-page">
      {tab.rows.map((row, index) => <div className="df-layout-list-item" key={row.id}>
        <div className="df-layout-insert-zone" onDragOver={(event) => { event.preventDefault(); event.stopPropagation(); }} onDrop={(event) => dropLayoutAtTab(event, index)}>لایه یا کنترل فاصله را اینجا قرار دهید</div>
        <LayoutPreview
          row={row}
          tabName={tab.name}
          form={form}
          selection={selection}
          depth={0}
          onChange={onChange}
          onSelect={onSelect}
        />
      </div>)}
      {!tab.rows.length ? <div className="df-empty-canvas">یک لایه از ستون ابزار اضافه کنید.</div> : null}
      <div className="df-canvas-layout-drop" onDragOver={(event) => { event.preventDefault(); event.stopPropagation(); }} onDrop={(event) => dropLayoutAtTab(event, tab.rows.length)}>برای انتقال لایه یا کنترل فاصله به انتهای مرحله، آن را اینجا رها کنید.</div>
    </div>
  </main>;
}
